import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import { DOMAIN_API_HOST } from "../../../configs/main";
import useAdminUser from "../../../data/useAdminUser";
import DataTable from "../Table/Datatable";
import "../Campaigns/Campaings.scss";

const METRICS_EMAILS = ["e.usikova@programmatic.ru", "a.tuhtaev@programmatic.ru"];
const PAGE_SIZE = 15;

const ellipsisStyle = {
  display: "block",
  flex: "1 1 auto",
  minWidth: 0,
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function isoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { dateFrom: isoDate(start), dateTo: isoDate(end) };
}

function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes <= 0) return `${rest} сек`;
  return `${minutes} мин ${rest} сек`;
}

function errorDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}

function Ellipsis({ value }) {
  const text = value || "";
  return (
    <span style={ellipsisStyle} title={text}>
      {text}
    </span>
  );
}

export default function DmpSiteVisits() {
  const { user, role, isLoading } = useAdminUser();
  const isManagerRetargeo = role === "MANAGER_RETARGEO";
  const initial = useMemo(defaultRange, []);
  const [dateFrom, setDateFrom] = useState(initial.dateFrom);
  const [dateTo, setDateTo] = useState(initial.dateTo);
  const [brand, setBrand] = useState(isManagerRetargeo ? "retargeo" : "programmatic");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const showMetrics = METRICS_EMAILS.includes((user?.email || "").toLowerCase());

  useEffect(() => {
    if (isManagerRetargeo) setBrand("retargeo");
  }, [isManagerRetargeo]);

  useEffect(() => {
    if (isLoading) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      brand,
      q: appliedQuery,
      page: String(page),
      limit: String(PAGE_SIZE),
    });
    fetch(`${DOMAIN_API_HOST}/api/v1/admin/dmp-site-visits/?${params}`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("admin_token") },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(errorDetail(data, "Не удалось загрузить визиты"));
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setRows(data.data || []);
        setTotalPages(data.total_pages || 1);
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setError(err.message || "Не удалось загрузить визиты");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, dateFrom, dateTo, brand, appliedQuery, page]);

  const columns = useMemo(() => [
    {
      Header: "Название кампании",
      accessor: "name",
      minWidth: 160,
      width: 260,
      maxWidth: 420,
      Cell: ({ value }) => <Ellipsis value={value} />,
    },
    {
      Header: "Почта клиента",
      accessor: "email",
      minWidth: 160,
      width: 240,
      maxWidth: 360,
      Cell: ({ value }) => <Ellipsis value={value} />,
    },
    {
      Header: "Юрлицо",
      accessor: "legal_entity",
      minWidth: 160,
      width: 240,
      maxWidth: 360,
      Cell: ({ value }) => <Ellipsis value={value || "—"} />,
    },
    {
      Header: "Сайт",
      accessor: "site",
      minWidth: 120,
      width: 180,
      maxWidth: 280,
      Cell: ({ value }) => <Ellipsis value={value || "—"} />,
    },
    {
      Header: "Клики",
      accessor: "visits",
      minWidth: 90,
      width: 120,
      maxWidth: 180,
      Cell: ({ row }) => {
        const prg = row.original.prg || [];
        const label = Number(row.original.visits || 0).toLocaleString("ru-RU");
        if (!prg.length) return label;
        return (
          <Tooltip
            title={
              <div>
                {prg.map((item) => (
                  <div key={item.code}>
                    {item.code}: {Number(item.visits || 0).toLocaleString("ru-RU")}
                  </div>
                ))}
              </div>
            }
          >
            <span style={{ borderBottom: "1px dashed #3f73f9", cursor: "default" }}>{label}</span>
          </Tooltip>
        );
      },
    },
    ...(showMetrics ? [
      {
        Header: "Отказность",
        accessor: "bounce_rate",
        minWidth: 100,
        width: 130,
        maxWidth: 180,
        Cell: ({ value }) => `${Number(value || 0).toLocaleString("ru-RU")}%`,
      },
      {
        Header: "Глубина",
        accessor: "avg_depth",
        minWidth: 90,
        width: 120,
        maxWidth: 160,
        Cell: ({ value }) => Number(value || 0).toLocaleString("ru-RU"),
      },
      {
        Header: "Время на сайте",
        accessor: "avg_duration_sec",
        minWidth: 120,
        width: 160,
        maxWidth: 220,
        Cell: ({ value }) => formatDuration(value),
      },
    ] : []),
  ], [showMetrics]);

  if (isLoading) return null;

  return (
    <div className="Campaigns">
      <div className="Campaigns-header">
        <div className="Campaigns-headerText">
          <h1>Визиты по PRG</h1>
          <p>Заходы с рекламных UTM на сайты с пикселем DMP</p>
        </div>
      </div>
      <div className="Campaigns-subbar">
        <div className="Campaigns-brands">
          {!isManagerRetargeo && (
            <button
              type="button"
              className={brand === "programmatic" ? "Campaigns-brandBtn is-active" : "Campaigns-brandBtn"}
              onClick={() => {
                setBrand("programmatic");
                setPage(1);
              }}
            >
              Programmatic
            </button>
          )}
          <button
            type="button"
            className={brand === "retargeo" ? "Campaigns-brandBtn is-active" : "Campaigns-brandBtn"}
            onClick={() => {
              setBrand("retargeo");
              setPage(1);
            }}
          >
            Retargeo
          </button>
        </div>
      </div>
      <div className="Campaigns-toolbar">
        <div className="Campaigns-toolbarLeft">
          <input
            className="Campaigns-quickInput"
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
          />
          <input
            className="Campaigns-quickInput"
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
          />
          <input
            className="Campaigns-quickInput is-search"
            value={query}
            placeholder="Кампания, почта, сайт или юрлицо"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setAppliedQuery(query.trim());
                setPage(1);
              }
            }}
          />
          <button
            type="button"
            className="Campaigns-filterBtn"
            onClick={() => {
              setAppliedQuery(query.trim());
              setPage(1);
            }}
          >
            Найти
          </button>
        </div>
      </div>
      <div className="Campaigns-content">
        {loading ? (
          <div className="Campaigns-loading">
            <div>Загружаем таблицу</div>
          </div>
        ) : error ? (
          <div className="Campaigns-loading">{error}</div>
        ) : (
          <>
            <div className="Campaigns-tablePanel">
              <div className="Campaigns-tableScroll">
                <DataTable data={rows} columns={columns} />
              </div>
            </div>
            <div className="Campaigns-pagination">
              <button
                type="button"
                className="Campaigns-pageBtn"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page <= 1}
              >
                Назад
              </button>
              <span className="Campaigns-pageInfo">{page} / {totalPages}</span>
              <button
                type="button"
                className="Campaigns-pageBtn"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page >= totalPages}
              >
                Вперёд
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
